/**
 * SurfacePolyGizmo — visual feedback for the Surface Poly tool.
 *
 *  - **Drawing stage**: polyline edges (LineSegments rebuilt from
 *    `draft.vertices` each frame); a rubber-band preview segment when a
 *    drag is in flight, colored by `preview.selfIntersects` /
 *    `preview.willClose` / default; two endpoint dots (sphere meshes
 *    sized via `screenToWorldRadius`) with default / hover /
 *    opposite-end-pulse / will-close color states.
 *
 *  - **Extrude stage**: the shared `<SurfaceExtrudeArrow>` interactive
 *    height handle — grabbing it begins the extrude drag and releasing
 *    confirms — plus a translucent prism wireframe (bottom polygon + top
 *    polygon + N vertical connectors) that scales live with the drag.
 *
 * Drawing-stage pointer events are owned by `SurfacePolyHandler`; the
 * extrude-stage drag is owned by the embedded extrude-arrow gizmo. The
 * "hovered endpoint" hint is read from the `surfacePolyUndoBridge` slot
 * via a per-frame poll (the bridge bumps a revision counter on change so
 * the gizmo can short-circuit).
 */

import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useMemo, useRef } from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../../app/store.ts";
import {
  selectSurfacePolyDraft,
  selectSurfacePolyStage,
} from "../../app/surfaceToolSelectors.ts";
import { SURFACE_PREVIEW_HEIGHT_EPSILON } from "../../modeling/surfaceConstants.ts";
import { screenToWorldRadius } from "../screenRadius.ts";
import { getSurfacePolyHoveredEndpoint } from "../surfacePolyUndoBridge.ts";
import {
  DEFAULT_OPACITY,
  GIZMO_SCREEN_PX,
  OP_HANDLE_COLOR,
  OP_HANDLE_COLOR_HOVER,
  SURFACE_CUT_PREVIEW_COLOR,
  SURFACE_CUT_PREVIEW_COLOR_HOVER,
} from "./constants.ts";
import { applyGizmoRenderOrder } from "./renderOrder.ts";
import { SurfaceExtrudeArrow } from "./SurfaceExtrudeArrow.tsx";
import {
  consumeSurfaceTargetPointer,
  SurfaceLoopEdgeTargetHits,
} from "./SurfaceProfileTargetHits.tsx";
import {
  applyLinePositions,
  buildSurfacePolyProfilePreview,
} from "./surfaceProfilePreview.ts";
import { useSurfaceToolGizmoCallbacks } from "./useSurfaceToolGizmoCallbacks.ts";

// ---------------------------------------------------------------------------
// Constants & shared geometries
// ---------------------------------------------------------------------------

const ENDPOINT_PX = 7;
/**
 * Hard upper bound on draftable polyline vertex count for buffer sizing.
 * Far above the polygon counts a user would draw by hand (typically <20)
 * — chosen to be generous enough that no on-screen interaction can
 * exhaust the pre-allocated polyline buffer.
 */
const MAX_POLY_VERTICES = 256;
const COLOR_DEFAULT = OP_HANDLE_COLOR;
const COLOR_PREVIEW = OP_HANDLE_COLOR;
const COLOR_INVALID = 0xff4040;
const COLOR_WILL_CLOSE = 0x80ff80;
const COLOR_ENDPOINT_DIM = 0x808080;
const COLOR_ENDPOINT_HOVER = 0xffff80;

// Endpoint dot — a small unit sphere; per-instance scale set in useFrame.
const ENDPOINT_GEOMETRY = new THREE.SphereGeometry(1, 12, 8);

const _upY = new THREE.Vector3(0, 1, 0);
const _scratchPos = new THREE.Vector3();
const _scratchNormal = new THREE.Vector3();

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SurfacePolyGizmo() {
  const stage = useStore(selectSurfacePolyStage);
  const draft = useStore(selectSurfacePolyDraft);
  const { camera, size: viewportSize } = useThree();

  const groupRef = useRef<THREE.Group>(null);
  const polylineRef = useRef<THREE.LineSegments>(null);
  const previewRef = useRef<THREE.LineSegments>(null);
  const headDotRef = useRef<THREE.Mesh>(null);
  const tailDotRef = useRef<THREE.Mesh>(null);
  const arrowGroupRef = useRef<THREE.Group>(null);
  const prismWireRef = useRef<THREE.LineSegments>(null);
  const prismWireGeometryRef = useRef(new THREE.BufferGeometry());

  const visible = stage !== null && draft !== null && draft.vertices.length > 0;
  const previewColor =
    draft && draft.height < 0 ? SURFACE_CUT_PREVIEW_COLOR : COLOR_DEFAULT;
  const previewHoverColor =
    draft && draft.height < 0
      ? SURFACE_CUT_PREVIEW_COLOR_HOVER
      : OP_HANDLE_COLOR_HOVER;

  // Shared per-kind callback factory — owns setHeight / setDragging /
  // confirm / setProfile{Face,Edge}. Same shape used by every surface
  // gizmo. See `SurfaceFootprintGizmo` for rationale on the camera lock.
  const callbacks = useSurfaceToolGizmoCallbacks("poly");
  const { setHeight, setDragging, confirm, setProfileFace, setProfileEdge } =
    callbacks;

  // Captured at pointerdown by `SurfaceExtrudeArrow`. Polyline centroid in
  // world space drives the height projection — undo / redo of segments may
  // shift it between draws, so we read live from the store.
  const computeAnchor = useCallback((): [number, number, number] | null => {
    const d = selectSurfacePolyDraft(useStore.getState());
    if (!d || d.vertices.length === 0) return null;
    return buildSurfacePolyProfilePreview(
      d.vertices,
      d.basis.normal,
      d.height,
      d.profileTarget,
    ).anchor;
  }, []);
  const computeNormal = useCallback((): [number, number, number] | null => {
    const d = selectSurfacePolyDraft(useStore.getState());
    return d ? d.basis.normal : null;
  }, []);

  // ── Polyline edge buffer ────────────────────────────────────────────────
  // Rebuilt each frame from `draft.vertices`. The Float32Array is sized
  // to a comfortable upper bound (matches MAX_POLY_VERTICES below) and
  // re-used across frames; each frame writes the active prefix and uses
  // setDrawRange to expose only the segments actually in play. Avoids
  // the GC churn of `new Float32Array(N * 6)` per frame during a 60Hz
  // drag, mirroring the cube/cylinder wire pattern in
  // `SurfaceFootprintGizmo`.
  const polylineGeometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(MAX_POLY_VERTICES * 6), 3),
    );
    return g;
  }, []);
  const polylinePositionsRef = useRef<Float32Array | null>(null);

  // Live preview ("rubber band" from drag-start endpoint to the candidate
  // tip during a pointermove). 2-vertex line.
  const previewGeometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(6), 3),
    );
    return g;
  }, []);

  // ── Per-frame: shape + transform updates ────────────────────────────────
  useFrame(() => {
    if (!visible || !draft) return;
    const verts = draft.vertices;
    const N = verts.length;

    // -- Polyline edge buffer ---------------------------------------------
    if (polylineRef.current) {
      // Build segments [v0,v1, v1,v2, ..., v_{N-2},v_{N-1}, (closed ? v_{N-1},v_0 : nothing)].
      const segCount = draft.closed ? N : Math.max(0, N - 1);
      if (!polylinePositionsRef.current) {
        polylinePositionsRef.current = polylineGeometry.attributes.position
          .array as Float32Array;
      }
      const positions = polylinePositionsRef.current;
      // Write the active prefix; setDrawRange exposes only those vertices
      // to the GPU even though the buffer itself holds room for more.
      for (let i = 0; i < N - 1 && i < MAX_POLY_VERTICES - 1; i += 1) {
        positions[i * 6 + 0] = verts[i][0];
        positions[i * 6 + 1] = verts[i][1];
        positions[i * 6 + 2] = verts[i][2];
        positions[i * 6 + 3] = verts[i + 1][0];
        positions[i * 6 + 4] = verts[i + 1][1];
        positions[i * 6 + 5] = verts[i + 1][2];
      }
      if (draft.closed && N >= 2 && N <= MAX_POLY_VERTICES) {
        const j = N - 1;
        positions[j * 6 + 0] = verts[N - 1][0];
        positions[j * 6 + 1] = verts[N - 1][1];
        positions[j * 6 + 2] = verts[N - 1][2];
        positions[j * 6 + 3] = verts[0][0];
        positions[j * 6 + 4] = verts[0][1];
        positions[j * 6 + 5] = verts[0][2];
      }
      polylineGeometry.attributes.position.needsUpdate = true;
      polylineGeometry.setDrawRange(0, segCount * 2);
      polylineRef.current.geometry = polylineGeometry;
      polylineRef.current.visible = segCount > 0;
    }

    // -- Preview segment ---------------------------------------------------
    const preview = draft.preview;
    if (previewRef.current && preview) {
      const startWorld = preview.fromEnd === "tail" ? verts[N - 1] : verts[0];
      const positions = previewGeometry.attributes.position
        .array as Float32Array;
      positions[0] = startWorld[0];
      positions[1] = startWorld[1];
      positions[2] = startWorld[2];
      positions[3] = preview.candidate[0];
      positions[4] = preview.candidate[1];
      positions[5] = preview.candidate[2];
      previewGeometry.attributes.position.needsUpdate = true;
      // Color reflects validity: invalid (red) > will-close (green) > default.
      const mat = previewRef.current.material as THREE.LineBasicMaterial;
      mat.color.setHex(
        preview.selfIntersects
          ? COLOR_INVALID
          : preview.willClose
            ? COLOR_WILL_CLOSE
            : COLOR_PREVIEW,
      );
      previewRef.current.visible = stage === "drawing";
    } else if (previewRef.current) {
      previewRef.current.visible = false;
    }

    // -- Endpoint dots -----------------------------------------------------
    // Hovered endpoint comes from the bridge slot. Read it directly each
    // frame; the gizmo unconditionally updates the dot materials below
    // anyway, so the previous rev-counter short-circuit was decorative.
    const hovered = getSurfacePolyHoveredEndpoint();

    const headDot = headDotRef.current;
    const tailDot = tailDotRef.current;
    if (headDot) {
      const pos = verts[0];
      _scratchPos.set(pos[0], pos[1], pos[2]);
      headDot.position.copy(_scratchPos);
      headDot.scale.setScalar(
        screenToWorldRadius(
          ENDPOINT_PX,
          _scratchPos,
          camera,
          viewportSize.height,
        ),
      );
      headDot.visible = !draft.closed && stage === "drawing";
      const mat = headDot.material as THREE.MeshBasicMaterial;
      mat.color.setHex(endpointColor("head", hovered, preview, draft.closed));
      mat.opacity = endpointOpacity("head", hovered, preview);
    }
    if (tailDot) {
      const pos = verts[N - 1];
      _scratchPos.set(pos[0], pos[1], pos[2]);
      tailDot.position.copy(_scratchPos);
      tailDot.scale.setScalar(
        screenToWorldRadius(
          ENDPOINT_PX,
          _scratchPos,
          camera,
          viewportSize.height,
        ),
      );
      // Tail collapses onto head on a 1-vertex polyline; hide to avoid Z-fight.
      tailDot.visible = !draft.closed && stage === "drawing" && N >= 2;
      const mat = tailDot.material as THREE.MeshBasicMaterial;
      mat.color.setHex(endpointColor("tail", hovered, preview, draft.closed));
      mat.opacity = endpointOpacity("tail", hovered, preview);
    }

    // -- Extrude stage: arrow + prism wireframe ---------------------------
    if (stage === "extrude" && draft.closed && N >= 3 && draft.basis) {
      // Centroid in world space — anchor for the arrow.
      let cx = 0;
      let cy = 0;
      let cz = 0;
      for (const v of verts) {
        cx += v[0];
        cy += v[1];
        cz += v[2];
      }
      cx /= N;
      cy /= N;
      cz /= N;
      _scratchPos.set(cx, cy, cz);
      _scratchNormal.set(
        draft.basis.normal[0],
        draft.basis.normal[1],
        draft.basis.normal[2],
      );
      const profilePreview = buildSurfacePolyProfilePreview(
        verts,
        draft.basis.normal,
        draft.height,
        draft.profileTarget,
      );
      if (arrowGroupRef.current) {
        _scratchPos.set(
          profilePreview.anchor[0],
          profilePreview.anchor[1],
          profilePreview.anchor[2],
        );
        arrowGroupRef.current.position.copy(_scratchPos);
        arrowGroupRef.current.quaternion.setFromUnitVectors(
          _upY,
          _scratchNormal,
        );
        arrowGroupRef.current.scale.setScalar(
          screenToWorldRadius(
            GIZMO_SCREEN_PX,
            _scratchPos,
            camera,
            viewportSize.height,
          ),
        );
        arrowGroupRef.current.visible = true;
      }
      // Prism wireframe — bottom polygon (the locked polyline) + top
      // polygon (vertices offset by `height` along normal) + vertical
      // connectors. Bottom is at the polyline plane; top at +height·normal.
      if (prismWireRef.current) {
        const h = draft.height;
        applyLinePositions(
          prismWireGeometryRef.current,
          profilePreview.wirePositions,
        );
        prismWireRef.current.geometry = prismWireGeometryRef.current;
        prismWireRef.current.visible =
          Math.abs(h) > SURFACE_PREVIEW_HEIGHT_EPSILON;
      }
    } else {
      if (arrowGroupRef.current) arrowGroupRef.current.visible = false;
      if (prismWireRef.current) prismWireRef.current.visible = false;
    }
  });

  // Render-order layering — same as OperatorGizmo / SurfaceFootprintGizmo so the
  // overlays draw above scene geometry. groupRef is stable across renders
  // so this only needs to run once on mount.
  useEffect(() => {
    const g = groupRef.current;
    if (!g) return;
    applyGizmoRenderOrder(g, 999);
  }, []);

  if (!visible || !draft) return null;

  return (
    <group ref={groupRef} userData={{ excludeFromExport: true }}>
      {/* Polyline (placed segments) */}
      <lineSegments ref={polylineRef} renderOrder={999}>
        <lineBasicMaterial
          color={previewColor}
          transparent
          opacity={DEFAULT_OPACITY}
          depthTest={false}
          depthWrite={false}
        />
      </lineSegments>

      {/* Live preview segment (rubber band) */}
      <lineSegments
        ref={previewRef}
        renderOrder={999}
        geometry={previewGeometry}
      >
        <lineBasicMaterial
          color={COLOR_PREVIEW}
          transparent
          opacity={DEFAULT_OPACITY}
          depthTest={false}
          depthWrite={false}
        />
      </lineSegments>

      {/* Head endpoint dot */}
      <mesh ref={headDotRef} geometry={ENDPOINT_GEOMETRY} renderOrder={999}>
        <meshBasicMaterial
          color={COLOR_ENDPOINT_DIM}
          transparent
          opacity={0.7}
          depthTest={false}
          depthWrite={false}
        />
      </mesh>

      {/* Tail endpoint dot */}
      <mesh ref={tailDotRef} geometry={ENDPOINT_GEOMETRY} renderOrder={999}>
        <meshBasicMaterial
          color={COLOR_ENDPOINT_DIM}
          transparent
          opacity={0.7}
          depthTest={false}
          depthWrite={false}
        />
      </mesh>

      {/* Live prism wireframe (extrude stage) */}
      <lineSegments ref={prismWireRef} renderOrder={999}>
        <lineBasicMaterial
          color={previewColor}
          transparent
          opacity={DEFAULT_OPACITY}
          depthTest={false}
          depthWrite={false}
        />
      </lineSegments>

      {/* Height-axis arrow (extrude stage) — interactive: grab to begin
          extrude, release to confirm (same pattern as OperatorGizmo). */}
      <SurfaceExtrudeArrow
        ref={arrowGroupRef}
        computeAnchor={computeAnchor}
        computeNormal={computeNormal}
        setHeight={setHeight}
        confirm={confirm}
        setDragging={setDragging}
        color={previewColor}
        hoverColor={previewHoverColor}
      />

      {stage === "extrude" && draft.closed && draft.vertices.length >= 3 ? (
        <SurfaceLoopEdgeTargetHits
          vertices={draft.vertices}
          onSelectFace={(e) => {
            consumeSurfaceTargetPointer(e);
            setProfileFace();
          }}
          onSelectEdge={(edgeIndex, e) => {
            consumeSurfaceTargetPointer(e);
            setProfileEdge(edgeIndex);
          }}
        />
      ) : null}
    </group>
  );
}

// ---------------------------------------------------------------------------
// Endpoint color / opacity rules
// ---------------------------------------------------------------------------
//
// Default = dim. Hovered (no drag) = bright. During a drag, the OPPOSITE
// endpoint of the active drag is the closure target; brighten it. When the
// preview reports `willClose`, swap to the green "will close" color so the
// user gets unambiguous feedback that release confirms the loop.

function endpointColor(
  end: "head" | "tail",
  hovered: "head" | "tail" | null,
  preview: { fromEnd: "head" | "tail"; willClose: boolean } | null,
  closed: boolean,
): number {
  if (closed) return COLOR_ENDPOINT_DIM;
  if (preview) {
    const opposite = preview.fromEnd === "head" ? "tail" : "head";
    if (end === opposite) {
      return preview.willClose ? COLOR_WILL_CLOSE : COLOR_ENDPOINT_HOVER;
    }
    return COLOR_ENDPOINT_DIM;
  }
  if (hovered === end) return COLOR_ENDPOINT_HOVER;
  return COLOR_ENDPOINT_DIM;
}

function endpointOpacity(
  end: "head" | "tail",
  hovered: "head" | "tail" | null,
  preview: { fromEnd: "head" | "tail" } | null,
): number {
  if (preview) {
    const opposite = preview.fromEnd === "head" ? "tail" : "head";
    return end === opposite ? 1.0 : 0.5;
  }
  return hovered === end ? 1.0 : 0.7;
}
